/**
 * GPU detection and memory budget calculation
 *
 * Uses navigator.deviceMemory for system RAM baseline, then adds
 * GPU-tier bonus based on detect-gpu library classification.
 */

import { getGPUTier, TierResult } from 'detect-gpu'

/**
 * GPU tier bonus in megabytes (added on top of base RAM budget)
 * - Tier 0: Integrated graphics - no bonus
 * - Tier 1: Entry discrete (GTX 1050, RX 560) - ~2GB VRAM
 * - Tier 2: Mid-range (RTX 3060) - ~6GB VRAM
 * - Tier 3: High-end (RTX 4080, Apple Silicon) - ~12GB+ VRAM
 */
const TIER_VRAM_BONUS_MB: Record<number, number> = {
  0: 0, // Integrated - no bonus
  1: 1024, // 1GB bonus
  2: 3072, // 3GB bonus
  3: 6144, // 6GB bonus
}

/**
 * Base budget is this fraction of system RAM
 */
const BASE_RAM_FRACTION = 0.8 // 80% of system RAM

/**
 * Use 90% of calculated budget to leave headroom for:
 * - Browser overhead
 * - Other tabs/applications
 * - OS compositor
 */
const BUDGET_UTILIZATION = 0.9

/**
 * Default budget if detection fails (2GB)
 */
const DEFAULT_BUDGET_MB = 2048

/**
 * localStorage key for storing GPU config
 */
export const GPU_CONFIG_KEY = 'martini_gpu_config'

/**
 * localStorage key for manual memory limit (in MB)
 */
export const MANUAL_MEMORY_LIMIT_KEY = 'martini_manual_memory_limit_mb'

/**
 * localStorage key for max video sinks setting
 */
export const MAX_VIDEO_SINKS_KEY = 'martini_max_video_sinks'

/**
 * localStorage key for video overlay enabled/disabled
 */
export const VIDEO_OVERLAY_ENABLED_KEY = 'martini_video_overlay_enabled'

/**
 * Default manual memory limit (6GB)
 */
export const DEFAULT_MANUAL_MEMORY_LIMIT_MB = 6 * 1024 // 6GB

/**
 * Default max video sinks.
 * Keep this conservative: each warm sink owns Mediabunny input/cache/decoder
 * resources, and large cloud projects can make broad prewarm expensive.
 */
export const DEFAULT_MAX_VIDEO_SINKS = 6

/**
 * Minimum max video sinks (1)
 * Allows minimal buffering for slow network scenarios
 */
export const MIN_MAX_VIDEO_SINKS = 1

/**
 * Maximum max video sinks (100)
 * Upper bound for power users with capable hardware
 */
export const MAX_MAX_VIDEO_SINKS = 100

/**
 * Minimum manual memory limit (200MB for low-memory devices or testing)
 */
export const MIN_MEMORY_LIMIT_MB = 200 // 200MB

/**
 * Maximum manual memory limit (54GB)
 */
export const MAX_MEMORY_LIMIT_MB = 54 * 1024 // 54GB

/**
 * WebGPU capability information
 */
export interface WebGPUCapability {
  /** Whether WebGPU is available in this browser. */
  supported: boolean
  /** GPU adapter description (e.g., "NVIDIA GeForce RTX 4080"). */
  adapterDescription?: string
  /** GPU vendor. */
  vendor?: string
  /** GPU architecture. */
  architecture?: string
  /** Maximum texture dimension (2D). */
  maxTextureDimension2D?: number
  /** Maximum buffer size in bytes. */
  maxBufferSize?: number
}

/**
 * Stored GPU configuration for debugging and display
 */
export interface StoredGpuConfig {
  detectedAt: string
  gpu: string
  tier: number
  isMobile: boolean
  type: string
  budgetMB: number
  rawBudgetMB: number
  deviceMemoryGB?: number
  userOverrideMB?: number
  /** WebGPU capability info (populated after probing). */
  webgpu?: WebGPUCapability
}

// Cached budget to avoid repeated async detection
let cachedBudgetBytes: number | null = null
let cachedGpuConfig: StoredGpuConfig | null = null
let cachedWebGPUCapability: WebGPUCapability | null = null

/**
 * Probe WebGPU support and adapter capabilities.
 * Non-blocking — returns a result with supported=false if unavailable.
 */
async function probeWebGPU(): Promise<WebGPUCapability> {
  if (typeof navigator === 'undefined' || !('gpu' in navigator)) {
    return { supported: false }
  }

  try {
    const gpu = navigator.gpu as GPU
    const adapter = await gpu.requestAdapter()
    if (!adapter) {
      return { supported: false }
    }

    const info = adapter.info
    const limits = adapter.limits

    return {
      supported: true,
      adapterDescription: info?.description || undefined,
      vendor: info?.vendor || undefined,
      architecture: info?.architecture || undefined,
      maxTextureDimension2D: limits.maxTextureDimension2D,
      maxBufferSize: Number(limits.maxBufferSize),
    }
  } catch {
    return { supported: false }
  }
}

/**
 * Check if WebGPU is available. Synchronous — returns cached result or false.
 */
export function isWebGPUAvailable(): boolean {
  return cachedWebGPUCapability?.supported ?? false
}

/**
 * Get stored GPU config from localStorage
 */
export function getStoredGpuConfig(): StoredGpuConfig | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(GPU_CONFIG_KEY)
    if (stored) {
      return JSON.parse(stored) as StoredGpuConfig
    }
  } catch {
    // Ignore localStorage errors
  }
  return null
}

/**
 * Store GPU config to localStorage
 */
function storeGpuConfig(config: StoredGpuConfig): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(GPU_CONFIG_KEY, JSON.stringify(config))
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get device memory in GB (navigator.deviceMemory)
 * Returns undefined if not available
 */
function getDeviceMemoryGB(): number | undefined {
  if (typeof navigator === 'undefined') return undefined
  // navigator.deviceMemory returns RAM in GB, rounded for privacy (0.25, 0.5, 1, 2, 4, 8)
  return (navigator as { deviceMemory?: number }).deviceMemory
}

/**
 * Detect if this is an Apple Silicon device with unified memory
 */
function isAppleSilicon(gpuTier: TierResult): boolean {
  const gpuName = gpuTier.gpu?.toLowerCase() ?? ''
  return (
    gpuName.includes('apple') ||
    gpuName.includes('m1') ||
    gpuName.includes('m2') ||
    gpuName.includes('m3') ||
    gpuName.includes('m4')
  )
}

/**
 * Calculate memory budget based on device memory and GPU tier
 * - Apple Silicon (unified memory): 80% of system RAM (no GPU bonus - it shares RAM)
 * - Discrete GPU: 80% of system RAM + GPU tier bonus
 */
function calculateBudgetMB(deviceMemoryGB: number | undefined, gpuTier: TierResult): number {
  const ramGB = deviceMemoryGB ?? 8 // Default to 8GB if unknown
  const baseFromRAM = ramGB * 1024 * BASE_RAM_FRACTION

  // Apple Silicon has unified memory - GPU shares RAM, so no bonus
  if (isAppleSilicon(gpuTier)) {
    return Math.round(baseFromRAM)
  }

  // Discrete GPU: add VRAM bonus
  const vramBonus = TIER_VRAM_BONUS_MB[gpuTier.tier] ?? 0
  return Math.round(baseFromRAM + vramBonus)
}

/**
 * Initialize memory budget using manual limit (primary) or GPU detection (fallback for display)
 * The manual limit is always used as the actual budget.
 * GPU detection is only done for informational purposes in the memory monitor.
 *
 * @returns Budget in bytes (frame cache portion of manual limit)
 */
export async function initializeMemoryBudget(): Promise<number> {
  // Always use manual limit for the actual budget
  const manualLimitMB = getManualMemoryLimitMB()
  const frameCacheBudgetMB = getFrameCacheBudgetMB()
  cachedBudgetBytes = frameCacheBudgetMB * 1024 * 1024

  // Still do GPU detection for informational purposes (memory monitor display)
  try {
    const storedConfig = getStoredGpuConfig()

    // Only re-detect if we haven't detected before or it's been a while
    if (!storedConfig || !cachedGpuConfig) {
      const deviceMemoryGB = getDeviceMemoryGB()
      const gpuTier: TierResult = await getGPUTier()

      // Calculate what the auto-detected budget would be (for display only)
      const rawBudgetMB = calculateBudgetMB(deviceMemoryGB, gpuTier)
      const detectedBudgetMB = Math.round(rawBudgetMB * BUDGET_UTILIZATION)

      const config: StoredGpuConfig = {
        detectedAt: new Date().toISOString(),
        gpu: gpuTier.gpu ?? 'unknown',
        tier: gpuTier.tier,
        isMobile: gpuTier.isMobile ?? false,
        type: gpuTier.type,
        budgetMB: detectedBudgetMB,
        rawBudgetMB: rawBudgetMB,
        deviceMemoryGB: deviceMemoryGB,
      }
      // Probe WebGPU capabilities (non-blocking, runs in parallel with budget calc)
      const webgpuCapability = await probeWebGPU()
      cachedWebGPUCapability = webgpuCapability
      config.webgpu = webgpuCapability

      storeGpuConfig(config)
      cachedGpuConfig = config

      const isUnified = isAppleSilicon(gpuTier)
      const bonusMB = isUnified ? 0 : (TIER_VRAM_BONUS_MB[gpuTier.tier] ?? 0)
      console.log(
        `[GPUDetection] GPU: ${gpuTier.gpu}, Tier: ${gpuTier.tier}, DeviceMemory: ${deviceMemoryGB ?? 'unknown'}GB, ` +
          `Unified: ${isUnified}, Detected: ${detectedBudgetMB}MB, Manual Limit: ${manualLimitMB}MB, Frame Cache: ${frameCacheBudgetMB}MB, ` +
          `WebGPU: ${webgpuCapability.supported ? `yes (${webgpuCapability.adapterDescription ?? 'unknown'})` : 'no'}`,
      )
    } else {
      cachedGpuConfig = storedConfig
      console.log(
        `[GPUDetection] Using cached GPU info. Manual Limit: ${manualLimitMB}MB, Frame Cache: ${frameCacheBudgetMB}MB`,
      )
    }
  } catch (error) {
    console.warn('[GPUDetection] GPU detection failed (informational only):', error)

    // Store fallback config for display
    const config: StoredGpuConfig = {
      detectedAt: new Date().toISOString(),
      gpu: 'detection-failed',
      tier: 1,
      isMobile: false,
      type: 'FALLBACK',
      budgetMB: Math.round(DEFAULT_BUDGET_MB * BUDGET_UTILIZATION),
      rawBudgetMB: DEFAULT_BUDGET_MB,
    }
    storeGpuConfig(config)
    cachedGpuConfig = config
  }

  return cachedBudgetBytes
}

/**
 * Get current frame cache memory budget in bytes (synchronous)
 * Always based on the manual memory limit
 */
export function getMemoryBudgetBytes(): number {
  // Calculate from manual limit with capped audio allocation
  return getFrameCacheBudgetMB() * 1024 * 1024
}

/**
 * Get cached GPU config (synchronous)
 */
export function getGpuConfig(): StoredGpuConfig | null {
  return cachedGpuConfig ?? getStoredGpuConfig()
}

/**
 * Get the manual memory limit from localStorage
 * @returns Memory limit in MB, or DEFAULT_MANUAL_MEMORY_LIMIT_MB if not set
 */
export function getManualMemoryLimitMB(): number {
  if (typeof window === 'undefined') return DEFAULT_MANUAL_MEMORY_LIMIT_MB

  try {
    const stored = localStorage.getItem(MANUAL_MEMORY_LIMIT_KEY)
    if (stored) {
      const value = parseInt(stored, 10)
      if (!isNaN(value) && value >= MIN_MEMORY_LIMIT_MB && value <= MAX_MEMORY_LIMIT_MB) {
        return value
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return DEFAULT_MANUAL_MEMORY_LIMIT_MB
}

/**
 * Set the manual memory limit
 * @param limitMB Memory limit in MB (clamped to MIN_MEMORY_LIMIT_MB - MAX_MEMORY_LIMIT_MB)
 */
export function setManualMemoryLimitMB(limitMB: number): void {
  if (typeof window === 'undefined') return

  const clamped = Math.max(MIN_MEMORY_LIMIT_MB, Math.min(MAX_MEMORY_LIMIT_MB, Math.round(limitMB)))

  try {
    localStorage.setItem(MANUAL_MEMORY_LIMIT_KEY, clamped.toString())
    // Update cached budget to use the new limit
    cachedBudgetBytes = clamped * 1024 * 1024
    console.log(`[GPUDetection] Manual memory limit set: ${clamped}MB`)

    // Dispatch event for listeners to update their budgets
    window.dispatchEvent(
      new CustomEvent('memory-limit-changed', {
        detail: { limitMB: clamped },
      }),
    )
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get the frame cache budget in MB (equals total memory limit)
 */
export function getFrameCacheBudgetMB(): number {
  return getManualMemoryLimitMB()
}

/**
 * Get the audio cache budget in MB (uncapped - uses full memory limit)
 * Audio cache is separate from frame cache and can use as much as needed.
 */
export function getAudioCacheBudgetMB(): number {
  return getManualMemoryLimitMB()
}

/**
 * Get the max video sinks setting from localStorage
 * @returns Max sinks count, or DEFAULT_MAX_VIDEO_SINKS if not set
 */
export function getMaxVideoSinks(): number {
  if (typeof window === 'undefined') return DEFAULT_MAX_VIDEO_SINKS

  try {
    const stored = localStorage.getItem(MAX_VIDEO_SINKS_KEY)
    if (stored) {
      const value = parseInt(stored, 10)
      if (!isNaN(value) && value >= MIN_MAX_VIDEO_SINKS && value <= MAX_MAX_VIDEO_SINKS) {
        return value
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return DEFAULT_MAX_VIDEO_SINKS
}

/**
 * Set the max video sinks limit
 * @param count Max sinks (clamped to MIN_MAX_VIDEO_SINKS - MAX_MAX_VIDEO_SINKS)
 */
export function setMaxVideoSinks(count: number): void {
  if (typeof window === 'undefined') return

  const clamped = Math.max(MIN_MAX_VIDEO_SINKS, Math.min(MAX_MAX_VIDEO_SINKS, Math.round(count)))

  try {
    localStorage.setItem(MAX_VIDEO_SINKS_KEY, clamped.toString())
    console.log(`[GPUDetection] Max video sinks set: ${clamped}`)

    // Dispatch event for listeners to update their limits
    window.dispatchEvent(
      new CustomEvent('max-sinks-changed', {
        detail: { maxSinks: clamped },
      }),
    )
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Get whether video overlays are enabled
 * @returns false by default (Martini Labs feature), true if explicitly enabled
 */
export function getVideoOverlayEnabled(): boolean {
  if (typeof window === 'undefined') return false

  try {
    const stored = localStorage.getItem(VIDEO_OVERLAY_ENABLED_KEY)
    if (stored !== null) {
      return stored === 'true'
    }
  } catch {
    // Ignore localStorage errors
  }
  return false // Default: disabled (Martini Labs feature)
}

/**
 * Set whether video overlays are enabled
 * @param enabled true to enable, false to disable
 */
export function setVideoOverlayEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem(VIDEO_OVERLAY_ENABLED_KEY, enabled.toString())
    console.log(`[GPUDetection] Video overlay enabled: ${enabled}`)

    // Dispatch event for components to react
    window.dispatchEvent(
      new CustomEvent('video-overlay-changed', {
        detail: { enabled },
      }),
    )
  } catch {
    // Ignore localStorage errors
  }
}
